import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiIdentificationCardLight } from 'react-icons/pi';
import { useAppDispatch } from '../../store';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import GlassCard from '../shared/brand/GlassCard';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import { LawyerProfile, errorMessage, upsertMyLawyerProfile } from '../../services/api/marketplaceService';

const splitList = (raw: string): string[] =>
  raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * M0 dev helper: create a lawyer profile for the current user so one person
 * can play both sides of the marketplace. M1 replaces it with the profile
 * builder + verification.
 */
const LawyerProfileForm: React.FC<{ onCreated: (p: LawyerProfile) => void }> = ({ onCreated }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [practiceAreas, setPracticeAreas] = useState('');
  const [jurisdictions, setJurisdictions] = useState('');
  const [saving, setSaving] = useState(false);

  const areas = splitList(practiceAreas);
  const juris = splitList(jurisdictions);

  const save = async () => {
    setSaving(true);
    try {
      onCreated(
        await upsertMyLawyerProfile({
          practice_areas: areas.map((a) => a.toLowerCase()),
          jurisdictions: juris.map((j) => j.toUpperCase()),
        })
      );
    } catch (err) {
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: 'error',
        duration: 4000,
        message: errorMessage(err, t('Could not save the lawyer profile')),
      }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <GlassCard
      icon={<PiIdentificationCardLight />}
      title={t('Create a lawyer profile')}
      subtitle={t('You need a lawyer profile to receive requests and send proposals.')}
    >
      <div className="space-y-4" data-testid="marketplace-profile-form">
        <Input
          type="text"
          id="marketplace-practice-areas"
          label={t('Practice areas')}
          placeholder={t('Comma separated, e.g. employment, tenancy')}
          value={practiceAreas}
          onChange={(e) => setPracticeAreas(e.target.value)}
          required
        />
        <Input
          type="text"
          id="marketplace-jurisdictions"
          label={t('Jurisdictions')}
          placeholder={t('Comma separated country codes, e.g. NO, SE')}
          value={jurisdictions}
          onChange={(e) => setJurisdictions(e.target.value)}
          required
        />
        <div className="flex justify-end border-t border-hairline-soft pt-4">
          <Button
            variant="primary"
            size="small"
            onClick={save}
            isLoading={saving}
            disabled={saving || areas.length === 0 || juris.length === 0}
          >
            {t('Create profile')}
          </Button>
        </div>
      </div>
    </GlassCard>
  );
};

export default LawyerProfileForm;
